import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import { useFirmReportSummary } from '../../../../../api/coreData'
import { useDenaliFilter } from '../../filters/DenaliFilterContextProvider'
import ErrorBoundary from '../../../../molecules/ErrorBoundary'
import RingChart from '../../../../charts/echarts/Ring'

const useFormatters = (valueFormat) => {
  const { formatter } = useFormattingContext()

  const valueFormatter = useCallback(d => {
    return formatter(d, valueFormat)
  }, [formatter, valueFormat])

  return {
    valueFormatter
  }
}

function DenaliMetricRing ({
  reportId,
  metrics,
  height,
  width,
  valueFormat,
  chartProps
}) {
  const { membershipFilter, filter } = useDenaliFilter()
  const { data, isLoading } = useFirmReportSummary(reportId, { membershipFilter, filters: filter })

  const chartData = useMemo(() => {
    if (!data?.summary?.length) return []

    return metrics.map((metric, idx) => {
      const dv = data.summary.reduce((p, c) => {
        p[c.key] = c
        return p
      }, {})

      const value = get(dv, metric.accessor)
      const pV = metric.target ? value / metric.target : value
      return {
        value: pV,
        name: metric.name || '',
        title: {
          offsetCenter: metric.name ? ['0%', '-10px'] : ['0%', '0']
        },
        detail: {
          valueAnimation: true,
          fontSize: metric.name ? undefined : 32,
          offsetCenter: metric.name ? ['0%', '10px'] : ['0%', '0']
        }
      }
    })
  }, [data, metrics])

  const { valueFormatter } = useFormatters(valueFormat)

  if (isLoading) {
    return null
  }

  return (
    <ErrorBoundary name='Ring Chart'>
      <RingChart
        height={height}
        width={width}
        data={chartData}
        valueFormat={valueFormatter}
        chartOptions={chartProps}
      />
    </ErrorBoundary>
  )
}

DenaliMetricRing.propTypes = {
  reportId: PropTypes.string,
  metrics: PropTypes.arrayOf(PropTypes.shape({
    accessor: PropTypes.string,
    name: PropTypes.string,
    target: PropTypes.string,
    targetType: PropTypes.string
  })),
  valueFormat: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  chartProps: PropTypes.object
}

DenaliMetricRing.defaultProps = {
  valueFormat: 'percentage',
  height: 300,
  width: 300
}

export default DenaliMetricRing
